/**
 * canvasAssurance - what the canvas is allowed to claim about a node.
 *
 * Package 8 (canvas honesty). Three questions, answered from node data only,
 * so the stylesheet, the filter effect and the hover line agree:
 *
 *   1. Was this node's risk ever assessed?  (`isRiskAssessed`)
 *      A node with no triage result, an unknown risk level, or a failed
 *      triage is "not assessed". It must never look like a scored-low node.
 *
 *   2. How certain is its entity attribution?  (`attributionCertainty`)
 *      Published / verified, provider label, heuristic, "Brand. User" cluster
 *      tag, or probabilistic. The backend carries method and tier in
 *      `attribution_method` / `attribution_tier` / `evidence_tier`, or the
 *      source in `entity_source`; the map below mirrors
 *      `analysis/entity_constants.py` (`ATTRIBUTION_METHODS`,
 *      `_METHOD_BY_SOURCE`, `is_generic_cluster_label`). Keep in step.
 *
 *   3. Which colour may a CRITICAL_ENDPOINT border take?
 *      (`criticalEndpointBorderColor`) Red only when the risk says so.
 *
 * Presentation stays in the stylesheet (R23): this module returns declarative
 * facts (`_risk_state`, `_attr_cert`) that selectors read.
 */

/** Normalise an entity type for comparison: case, spaces and hyphens folded. */
export function normalizeEntityTypeKey(t: unknown): string {
  if (typeof t !== 'string') return '';
  return t.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

/**
 * Entity types that put a node in Tier 1 (always visible). Stored in the
 * normalised form; test membership through `isTier1EntityType`, never by
 * raw `Set.has` on a node's `entity_type` (casing differs by source).
 */
export const TIER1_ENTITY_KEYS: ReadonlySet<string> = new Set([
  'exchange', 'high_risk_exchange', 'no_kyc_exchange',
  'sanctioned', 'sanction',
  'scam', 'fraud', 'phishing',
  'mixer', 'hacker', 'ransomware', 'exploit',
  'darknet', 'darknet_market',
  'p2p',
]);

export function isTier1EntityType(t: unknown): boolean {
  return TIER1_ENTITY_KEYS.has(normalizeEntityTypeKey(t));
}

// -- 1. Risk assessed? ------------------------------------------------------

const KNOWN_LEVELS = new Set(['low', 'medium', 'high', 'critical', 'severe']);

/** Types that are themselves a risk statement (a sanctions or crime label). */
const RISK_BEARING_TYPES = new Set([
  'sanctioned', 'sanction', 'scam', 'fraud', 'phishing', 'mixer', 'hacker',
  'ransomware', 'exploit', 'darknet', 'darknet_market', 'high_risk_exchange', 'mule',
]);

function level(v: unknown): string {
  return typeof v === 'string' ? v.trim().toLowerCase() : '';
}

/**
 * True when something real says how risky this node is. The absence of a
 * `risk_score` is not "low"; it is "not checked".
 */
export function isRiskAssessed(d: any): boolean {
  if (!d) return false;
  if (d.risk_assessed === true) return true;
  if (d.risk_assessed === false) return false;
  if (d.triage_failed === true) return false;
  const ts = level(d.triage_status);
  if (ts === 'failed' || ts === 'error') return false;
  if (KNOWN_LEVELS.has(level(d.risk_level)) || KNOWN_LEVELS.has(level(d.risk_tier))) return true;
  if (typeof d.risk_score === 'number' && Number.isFinite(d.risk_score)) return true;
  if (d.entity_category === 'sanction' || d.entity_category === 'scam') return true;
  if (RISK_BEARING_TYPES.has(normalizeEntityTypeKey(d.entity_type))) return true;
  return false;
}

/** Bucket used by the risk-level filter chips. */
export type RiskBucket = 'low' | 'medium' | 'high' | 'critical' | 'not_assessed';

export function riskBucket(d: any): RiskBucket {
  if (!isRiskAssessed(d)) return 'not_assessed';
  const lv = level(d.risk_level) || level(d.risk_tier);
  if (lv === 'severe') return 'critical';
  if (KNOWN_LEVELS.has(lv)) return lv as RiskBucket;
  if (typeof d.risk_score === 'number' && Number.isFinite(d.risk_score)) {
    const s = d.risk_score;
    return s >= 0.85 ? 'critical' : s >= 0.7 ? 'high' : s >= 0.3 ? 'medium' : 'low';
  }
  // Assessed by type or category only (sanctions label etc.)
  return 'high';
}

// -- 2. Attribution certainty -----------------------------------------------

export type AttributionCertainty =
  | 'verified'     // published by the exchange / sanctions list / analyst / proof of reserves
  | 'provider'     // third-party label (vendor, community, tagpack)
  | 'heuristic'    // inferred by our fingerprint or proximity logic
  | 'cluster'      // "Brand. User" counterparty-of-cluster tag, not ownership
  | 'probable'     // intelligence_attributions guess, no entity name
  | 'unspecified'; // attributed, but the node carries no method or tier

export interface AttributionInfo {
  certainty: AttributionCertainty;
  method: string | null;
  tier: string | null;
  /** One line for the hover text. */
  text: string;
}

const METHOD_CERTAINTY: Record<string, AttributionCertainty> = {
  cryptographic_por: 'verified',
  sanctions_list: 'verified',
  manual_le: 'verified',
  exchange_published: 'verified',
  vendor_label: 'provider',
  graphsense_tagpack: 'provider',
  community_label: 'provider',
  heuristic_fingerprint: 'heuristic',
  unknown_attribution: 'unspecified',
};

const METHOD_TIER: Record<string, string> = {
  cryptographic_por: 'crypto_proof',
  sanctions_list: 'service_assertion',
  manual_le: 'service_assertion',
  exchange_published: 'service_assertion',
  vendor_label: 'service_assertion',
  graphsense_tagpack: 'service_assertion',
  community_label: 'chain_fact',
  heuristic_fingerprint: 'heuristic',
  unknown_attribution: 'chain_fact',
};

/** Mirror of `_METHOD_BY_SOURCE` (most specific first). */
const METHOD_BY_SOURCE: Array<[string, string]> = [
  ['proof_of_reserves', 'cryptographic_por'], ['_por', 'cryptographic_por'], ['por_', 'cryptographic_por'],
  ['ofac', 'sanctions_list'], ['opensanctions', 'sanctions_list'], ['sanction', 'sanctions_list'],
  ['sdn', 'sanctions_list'], ['tether_blacklist', 'sanctions_list'], ['un_sc', 'sanctions_list'],
  ['manual', 'manual_le'], ['verified', 'manual_le'], ['analyst', 'manual_le'],
  ['indiankanoon', 'manual_le'], ['ecourts', 'manual_le'], ['news_scrape', 'manual_le'],
  ['arkham', 'vendor_label'], ['metasleuth', 'vendor_label'], ['nansen', 'vendor_label'],
  ['misttrack', 'vendor_label'], ['crystal', 'vendor_label'], ['chainalysis', 'vendor_label'],
  ['graphsense', 'graphsense_tagpack'],
  ['etherscan', 'community_label'], ['dawsbot', 'community_label'], ['coincarp', 'community_label'],
  ['dune', 'community_label'], ['tronscan', 'community_label'], ['walletexplorer', 'community_label'],
  ['oklink', 'community_label'], ['goplus', 'community_label'], ['defillama', 'community_label'],
  ['community', 'community_label'], ['scamsniffer', 'community_label'], ['ransomwh', 'community_label'],
  ['heuristic', 'heuristic_fingerprint'], ['hot_wallet_expansion', 'heuristic_fingerprint'],
  ['discovered', 'heuristic_fingerprint'], ['proximity', 'heuristic_fingerprint'],
  ['inference', 'heuristic_fingerprint'], ['exchange_detect', 'heuristic_fingerprint'],
];

export function methodFromSource(source: unknown): string | null {
  if (typeof source !== 'string' || !source) return null;
  const s = source.toLowerCase();
  for (const [needle, m] of METHOD_BY_SOURCE) if (s.includes(needle)) return m;
  return null;
}

/** Mirror of `is_generic_cluster_label` (the OKLink/Arkham "Brand. User" tag). */
const GENERIC_USER_SUFFIX = /[.\s\-_]+users?(?:[.\s\-_]*v?\d+)?\s*$/i;
export function isGenericClusterLabel(name: unknown): boolean {
  return typeof name === 'string' && GENERIC_USER_SUFFIX.test(name.trim());
}

const TIER_CERTAINTY: Record<string, AttributionCertainty> = {
  crypto_proof: 'verified',
  service_assertion: 'provider',
  chain_fact: 'provider',
  endpoint_artifact: 'provider',
  heuristic: 'heuristic',
};

const CERTAINTY_WORD: Record<AttributionCertainty, string> = {
  verified: 'Verified',
  provider: 'Provider label',
  heuristic: 'Heuristic',
  cluster: 'Cluster tag, not ownership',
  probable: 'Probable',
  unspecified: 'Attributed, method not recorded',
};

function hasName(d: any): boolean {
  const n = typeof d?.entity_name === 'string' ? d.entity_name.trim() : '';
  return !!n && n.toLowerCase() !== 'unknown';
}

/**
 * How firmly the node's entity attribution is backed. Returns null when the
 * node carries no attribution at all.
 */
export function attributionCertainty(d: any): AttributionInfo | null {
  if (!d) return null;
  const attrs: any[] = Array.isArray(d.intelligence_attributions) ? d.intelligence_attributions : [];
  const named = hasName(d);
  if (!named && attrs.length === 0) return null;

  // Method: explicit field, else derived from the recorded source.
  let method: string | null = typeof d.attribution_method === 'string' && d.attribution_method
    ? d.attribution_method : null;
  if (!method) method = methodFromSource(d.entity_source);
  if (!method) {
    for (const a of attrs) {
      const m = (typeof a?.method === 'string' && a.method) ? a.method : methodFromSource(a?.source);
      if (m) { method = m; break; }
    }
  }
  const tierField = [d.attribution_tier, d.evidence_tier].find(t => typeof t === 'string' && t) as string | undefined;
  const tier = tierField ?? (method ? METHOD_TIER[method] ?? null : null);

  let certainty: AttributionCertainty;
  if (!named) {
    certainty = 'probable';
  } else if (method && METHOD_CERTAINTY[method]) {
    certainty = METHOD_CERTAINTY[method];
  } else if (tier && TIER_CERTAINTY[tier]) {
    certainty = TIER_CERTAINTY[tier];
  } else {
    certainty = 'unspecified';
  }

  // A "Brand. User" label is a counterparty tag; only an analyst or proof can
  // override that reading.
  if (named && isGenericClusterLabel(d.entity_name)
      && method !== 'manual_le' && method !== 'cryptographic_por') {
    certainty = 'cluster';
  }

  const parts = [CERTAINTY_WORD[certainty]];
  if (method && certainty !== 'unspecified') parts.push(method);
  if (tier && certainty !== 'unspecified') parts.push(tier);
  return { certainty, method, tier, text: parts.join(' · ') };
}

// -- 3. CRITICAL_ENDPOINT border colour -------------------------------------

export const BORDER_GREEN = '#22c55e';
export const BORDER_AMBER = '#f59e0b';
export const BORDER_RED = '#ef4444';
export const BORDER_NEUTRAL = '#94a3b8';

/**
 * Red only when the risk says so. An exchange (any casing) with no adverse
 * risk reads green; a node nobody assessed reads neutral grey, never red.
 */
export function criticalEndpointBorderColor(d: any): string {
  if (!isRiskAssessed(d)) return BORDER_NEUTRAL;
  const lv = level(d.risk_tier) || level(d.risk_level);
  if (lv === 'low') return BORDER_GREEN;
  if (lv === 'medium') return BORDER_AMBER;
  if (lv === 'high' || lv === 'critical' || lv === 'severe') return BORDER_RED;
  if (typeof d.risk_score === 'number' && Number.isFinite(d.risk_score)) {
    return d.risk_score >= 0.7 ? BORDER_RED : d.risk_score >= 0.3 ? BORDER_AMBER : BORDER_GREEN;
  }
  // Assessed by type or category only.
  const key = normalizeEntityTypeKey(d.entity_type);
  if (key === 'high_risk_exchange') return BORDER_RED;
  if (key === 'no_kyc_exchange') return BORDER_AMBER;
  if (key === 'exchange') return BORDER_GREEN;
  return BORDER_RED;
}

// -- Stamping for the stylesheet --------------------------------------------

/**
 * Add `_risk_state` and `_attr_cert` to every node's data so the stylesheet
 * can select on data rather than on classes. Edges pass through untouched.
 * Returns a new array; the input is not mutated.
 */
export function stampAssurance<T extends { data?: any; group?: string }>(elements: T[]): T[] {
  return elements.map(el => {
    const d = el?.data;
    if (!d || el.group === 'edges' || d.source) return el;
    const state = isRiskAssessed(d) ? 'assessed' : 'not_assessed';
    const info = attributionCertainty(d);
    const cert = info ? info.certainty : '';
    if (d._risk_state === state && d._attr_cert === cert) return el;
    return { ...el, data: { ...d, _risk_state: state, _attr_cert: cert } };
  });
}
